import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import { Loader } from '@openx/components-core/lib/Loader/Loader';
import type { CustomVariablesDataPayload, SegmentsOptions } from '@openx/types/targeting/customVariables';
import type { TargetingProps } from '@openx/types/targeting/targeting';
import { noop } from '@openx/utils/noop';
import { type JSX, memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTargetingContext } from '../../utils/context/TargetingContext';
import { useDropdownOpen } from '../shared/ReadOnlyTargetingDropdown/hooks/useDropdownOpen';
import { StyledReadOnlyTargetingDropdown } from '../shared/ReadOnlyTargetingDropdown/ReadOnlyTargetingDropdown';
import { TargetingDropdownSection } from '../shared/ReadOnlyTargetingDropdown/TargetingDropdownSection';
import { useDrawer } from '../shared/TargetingDrawer/useDrawer';
import { TargetingReadOnly } from '../shared/TargetingReadOnly/TargetingReadOnly';
import { ContextualSegmentsMisconfigurationAdornment } from './ContextualSegmentsMisconfigurationAdornment';
import { ContextualSegmentsReadOnlyItems } from './ContextualSegmentsReadOnlyItems';
import { ContextualSegmentsFeesAlert } from './components/ContextualSegmentsFeesAlert';
import { ContextualSegmentsSelectionDrawer } from './drawer/ContextualSegmentsSelectionDrawer';
import { useGetContextualSummaryData } from './hooks/useGetContextualSummaryData';
import { useNoContextualPricing } from './utils/contextualPricing';
import {
  buildProviderNamesMap,
  customFormatToEnrichedSegments,
  type EnrichedProviderSegments,
  enrichedSegmentsToCustomFormat,
} from './utils/enrichment';
import { buildContextualMisconfigurationContext, isContextualSegmentMisconfigured } from './utils/misconfiguration';

export const ContextualSegmentsTargeting = memo(function ContextualSegmentsTargeting({
  targetingParams,
  onFieldUpdate = noop,
  field,
  name,
  accountUid = '',
  readonly,
}: TargetingProps<CustomVariablesDataPayload[]>): JSX.Element {
  const { t } = useTranslation();
  const { useGetContextualPricing = useNoContextualPricing } = useTargetingContext();
  const { data: contextualPricing } = useGetContextualPricing({ segments: targetingParams, accountUid });

  const {
    loading: segmentsLoading,
    segments,
    providerIds,
    cacheSegmentMetadata,
  } = useGetContextualSummaryData(accountUid, targetingParams || []);

  const providerNamesMap = useMemo(() => buildProviderNamesMap(segments ?? [], providerIds), [segments, providerIds]);
  const misconfigurationContext = useMemo(
    () => buildContextualMisconfigurationContext(segments ?? [], providerIds),
    [providerIds, segments]
  );

  // Convert API format to enriched segments on initialization
  const [enrichedParams, setEnrichedParams] = useState<EnrichedProviderSegments[]>(() =>
    customFormatToEnrichedSegments(targetingParams || [], segments ?? [], providerIds, providerNamesMap)
  );

  // Re-enrich when targeting params change from parent
  useEffect(() => {
    setEnrichedParams(
      customFormatToEnrichedSegments(targetingParams || [], segments ?? [], providerIds, providerNamesMap)
    );
  }, [targetingParams, segments, providerIds, providerNamesMap]);

  const enrichedSegments = useMemo(() => {
    if (segmentsLoading) {
      return [];
    }

    // Convert enriched params to EnrichedSegment[] format for display
    return enrichedParams.flatMap(selection => [
      ...selection.includes.map(segment => ({
        id: segment.id,
        providerId: selection.providerId,
        inclusionType: 'includes' as const,
        metadata: segment,
        isMisconfigured: isContextualSegmentMisconfigured(
          { providerId: selection.providerId, segmentId: segment.id },
          misconfigurationContext
        ),
      })),
      ...selection.excludes.map(segment => ({
        id: segment.id,
        providerId: selection.providerId,
        inclusionType: 'excludes' as const,
        metadata: segment,
        isMisconfigured: isContextualSegmentMisconfigured(
          { providerId: selection.providerId, segmentId: segment.id },
          misconfigurationContext
        ),
      })),
    ]);
  }, [enrichedParams, misconfigurationContext, segmentsLoading]);

  const hasMisconfiguredSegments = enrichedSegments.some(segment => segment.isMisconfigured);
  const misconfigurationMessage = t(
    'Some contextual segments do not match the contextual provider connected to this account and will not deliver. Review the segments marked with an icon and replace or remove them.'
  );

  const {
    isOpen: isDrawerOpen,
    onDrawerClose,
    onDrawerOpen,
  } = useDrawer({
    onApply: () => {
      // Convert enriched format to API format for parent
      const apiFormat = enrichedSegmentsToCustomFormat(enrichedParams);
      onFieldUpdate(field, apiFormat);
    },
    restoreParams: () =>
      setEnrichedParams(
        customFormatToEnrichedSegments(targetingParams || [], segments ?? [], providerIds, providerNamesMap)
      ),
  });

  const onChange = useCallback(
    (updater: React.SetStateAction<EnrichedProviderSegments[]>) => {
      const newEnriched = typeof updater === 'function' ? updater(enrichedParams) : updater;
      setEnrichedParams(newEnriched);
      // Convert to API format for parent
      const apiFormat = enrichedSegmentsToCustomFormat(newEnriched);
      onFieldUpdate(field, apiFormat);
    },
    [enrichedParams, field, onFieldUpdate]
  );

  const onRemoveAll = useCallback(() => {
    const emptyEnriched: EnrichedProviderSegments[] = [];
    setEnrichedParams(emptyEnriched);
    onFieldUpdate(field, []);
  }, [field, onFieldUpdate]);

  const hasSegments = useMemo(() => {
    return enrichedParams.length > 0 && enrichedParams.some(p => p.includes.length > 0 || p.excludes.length > 0);
  }, [enrichedParams]);

  const isEmpty = !hasSegments;

  const optionsCount = useMemo(() => {
    return enrichedSegments.length;
  }, [enrichedSegments]);

  const canOpen = !segmentsLoading && hasSegments;
  const { isOpen, toggleOpen } = useDropdownOpen(canOpen);

  const handleDrawerClose = useCallback(() => {
    setEnrichedParams(
      customFormatToEnrichedSegments(targetingParams || [], segments ?? [], providerIds, providerNamesMap)
    );
    onDrawerClose();
  }, [targetingParams, segments, providerIds, providerNamesMap, onDrawerClose]);

  const handleDrawerApply = useCallback(
    (newEnriched: EnrichedProviderSegments[]) => {
      setEnrichedParams(newEnriched);
      const mappedSegments: SegmentsOptions = newEnriched.flatMap(selection =>
        [...selection.includes, ...selection.excludes]
          .filter(segment => segment.name !== segment.id)
          .map(segment => ({ ...segment, contextual_provider: selection.providerId }))
      );
      cacheSegmentMetadata(mappedSegments);
      // Convert to API format for parent
      const apiFormat = enrichedSegmentsToCustomFormat(newEnriched);
      onFieldUpdate(field, apiFormat);
      onDrawerClose();
    },
    [cacheSegmentMetadata, field, onFieldUpdate, onDrawerClose]
  );

  return (
    <>
      <TargetingReadOnly
        name={name}
        field={field}
        isEmpty={isEmpty}
        onOpenDrawerClick={onDrawerOpen}
        onRemove={onRemoveAll}
        readonly={readonly}
        description={t('Target users based on contextual segments from integrated data providers')}
        isSingleChoice
        addButtonPendoFeature="contextual-segments-add"
        pendoFeature="contextual-segments-section"
        optionsCount={optionsCount}
        isOpen={isOpen}
        toggleOpen={toggleOpen}
        isDisabled={segmentsLoading}
        titleAdornment={hasMisconfiguredSegments ? <ContextualSegmentsMisconfigurationAdornment /> : undefined}
        actionButtonOverride={
          segmentsLoading ? (
            <CircularProgress
              size={20}
              thickness={4}
              sx={{ color: 'text.secondary' }}
              data-test="contextual-segments-loader"
            />
          ) : undefined
        }
      >
        {readonly ? segmentsLoading ? <Loader data-test="contextual-segments-loader" /> : null : null}

        {!isEmpty && isOpen && (
          <StyledReadOnlyTargetingDropdown
            sx={readonly ? { backgroundColor: 'background.paper', borderTop: 'none', borderBottom: 'none' } : undefined}
          >
            <TargetingDropdownSection
              name={t('Contextual Segments')}
              isEmpty={false}
              onOpenDrawerClick={onDrawerOpen}
              onRemove={onRemoveAll}
              isDisabled={segmentsLoading}
              hideButtons={readonly}
            >
              {hasMisconfiguredSegments && (
                <Alert severity="warning" sx={{ mb: 2 }} data-test="contextual-segments-misconfiguration-alert">
                  {misconfigurationMessage}
                </Alert>
              )}
              <ContextualSegmentsReadOnlyItems
                enrichedSegments={enrichedSegments}
                onChange={onChange}
                readonly={readonly}
              />
              <ContextualSegmentsFeesAlert contextualPricing={contextualPricing} />
            </TargetingDropdownSection>
          </StyledReadOnlyTargetingDropdown>
        )}
      </TargetingReadOnly>

      {isDrawerOpen ? (
        <ContextualSegmentsSelectionDrawer
          isOpen={isDrawerOpen}
          onClose={handleDrawerClose}
          onApply={handleDrawerApply}
          enrichedSegments={enrichedParams}
          accountUid={accountUid}
          readonly={readonly}
        />
      ) : null}
    </>
  );
});
